'use client'
import { Icon, PopupWrapper } from '@/components'
import yup from '@/utils/yup-global'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Dialog, DialogContent, Grid, IconButton, Stack, Typography } from '@mui/material'
import { AnimatePresence } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useForm } from 'react-hook-form'
import { useSizeGuideContext } from '../../contexts/side-guide-context'
import { getRecommendedSize } from '../../utils/sizes'
import { useSizeGuideSchema } from '../../validation/size-guides'
import { LIST_SHAPES, SIZE_CHART } from '../sizes/data-sample'
import { FormHeightAndWeight } from './form-height-and-weight'
import { RecommendSizes } from './recommend-sizes'
import { SizesTable } from './sizes-table'
import { updateBodyMetrics } from '@/features/auth'
import { useAccountStore } from '@/store'
import { useEffect } from 'react'

interface SizeGuidesProps {
  listShapes?: typeof LIST_SHAPES
  sizeChart?: typeof SIZE_CHART
}

const SHOW_SUGGEST_SIZE = false

export const SizeGuides = ({
  product,
  listShapes = LIST_SHAPES,
  sizeChart = SIZE_CHART,
}: SizeGuidesProps & { product: Product }) => {
  const t = useTranslations()
  const schema = useSizeGuideSchema()
  const { data: dataAccount } = useAccountStore()
  const { setRecommendedSizes, handleOpenRecommendedSizes, recommendedSizes, openRecommendedSizes } =
    useSizeGuideContext()

  const method = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: {
      shape: listShapes[0]?.value || 'balanced',
    },
  })

  const { handleSubmit, watch, setValue } = method
  const currentShape = watch('shape')

  useEffect(() => {
    const height = dataAccount?.body_metrics?.height
    const weight = dataAccount?.body_metrics?.weight
    if (dataAccount?.body_metrics && height && weight) {
      setValue('height', height)
      setValue('weight', weight)
      const sizes = getRecommendedSize(weight, height, 'balanced', sizeChart)
      setRecommendedSizes(sizes)
    }
  }, [dataAccount, setRecommendedSizes, setValue, sizeChart])

  const onSubmit = (data: yup.InferType<typeof schema>) => {
    // const bmi = calculateBMI(data.weight, data.height)
    const sizes = getRecommendedSize(data.weight, data.height, data.shape, sizeChart)
    if (
      data.weight != dataAccount?.body_metrics?.weight ||
      data.height != dataAccount?.body_metrics?.height
    ) {
      updateBodyMetrics({
        weight: data.weight,
        height: data.height,
        gender: dataAccount?.body_metrics?.gender || 0,
      })
    }
    setRecommendedSizes(sizes)
    handleOpenRecommendedSizes()
    // handleAssignRecommendedSize(sizes)
    // console.log('BMI:', bmi.toFixed(1))
  }

  return (
    <Stack spacing={2} component-name="size-guides">
      {/* form height and weight */}
      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* body shapes */}
        {SHOW_SUGGEST_SIZE && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormHeightAndWeight methods={method} onSubmit={handleSubmit(onSubmit)} />

            <Stack spacing={1}>
              <Typography variant="body2" fontWeight={300}>
                {t('Dáng người của bạn')}:
              </Typography>
              <Grid container spacing={1}>
                {LIST_SHAPES.map((shape) => (
                  <Grid key={shape?.label} size={4}>
                    <Stack
                      spacing={1}
                      alignItems={'center'}
                      onClick={() => setValue('shape', shape?.value)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          aspectRatio: '1/1',
                          position: 'relative',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '2px solid',
                          borderColor: currentShape === shape?.value ? 'primary.main' : 'transparent',
                        }}
                      >
                        <Image
                          src={shape?.image}
                          alt={shape?.label}
                          fill
                          sizes="(max-width: 768px) 100vw, 50vw"
                          style={{ objectFit: 'cover' }}
                        />
                      </Box>
                      <Typography variant="body2" textAlign={'center'} fontWeight={300} mt={1}>
                        {shape?.label}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>

            <Button
              variant="contained"
              sx={{
                color: 'primary.contrastText',
                textTransform: 'unset',
                padding: '12px 24px',
                borderRadius: '999px',
              }}
              onClick={handleSubmit(onSubmit)}
            >
              {t('Xem gợi ý chọn size')}
            </Button>

            {/* recommended sizes */}
            <AnimatePresence>
              {openRecommendedSizes && <RecommendSizes sizes={recommendedSizes} />}
            </AnimatePresence>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: SHOW_SUGGEST_SIZE ? 6 : 12 }}>
          {/* size table */}
          <SizesTable product={product} listSizes={sizeChart} />
        </Grid>
      </Grid>
    </Stack>
  )
}

type Props = {
  product: Product
}

// Size Guide Dialog Component
export const SizeGuideDialog = ({ product }: Props) => {
  const t = useTranslations()
  const { handleCloseSideGuide, openSideGuide } = useSizeGuideContext()

  return (
    <PopupWrapper
      isOpen={openSideGuide}
      handleClose={handleCloseSideGuide}
      width={SHOW_SUGGEST_SIZE ? '1056px' : '600px'}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={'12px'} alignItems="center">
          <IconButton onClick={handleCloseSideGuide} size="small">
            <Icon.ArrowRight
              sx={{ fontSize: '1.5rem', transform: 'rotate(180deg)', color: 'primary.main' }}
            />
          </IconButton>
          <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.3, fontWeight: 600 }}>
            {t('Footer.bottom.size-guide')}
          </Typography>
        </Stack>
        <SizeGuides product={product} />
      </Stack>
    </PopupWrapper>
  )
}
